import { useFocusEffect } from 'expo-router';
import { useCallback, useRef, useState } from 'react';
import { Input, type TamaguiElement, Text, YStack } from 'tamagui';

/**
 * PSInput — a single-line text field (atom).
 *
 * The form input from the designs: a bordered box with a placeholder, a focused
 * state, and an error state. Wraps Tamagui's `Input` (from `@tamagui/input`) so
 * we inherit real `TextInput` behavior, but restyles it entirely in our tokens.
 *
 * Three things worth reading:
 *
 * 1. We DON'T lean on Tamagui's `size` variant. That variant reads the `size`
 *    token group for height/padding/font, and this repo deliberately leaves that
 *    group unpopulated (see constants/tokens.ts). So height/px/fontSize are set
 *    explicitly here, the same way PSButton pins its own dimensions.
 *
 * 2. Focus is tracked in local state and only drives the BORDER color — a
 *    color-only flip like DateSelector's `selected` (hairline width is constant,
 *    so the box never resizes between states). Tamagui's built-in
 *    `focusStyle.borderColor` points at `$borderColorFocus`, which our single
 *    `light` theme doesn't define, so we own the focus look instead of relying
 *    on it. This `focused` boolean is UI state, not business logic — it's about
 *    how the atom looks, not about the value's meaning.
 *
 * 3. Error is render-only. The SCREEN validates and passes an `error` message
 *    string; PSInput just wears the red border + prints the message. The atom
 *    never knows what makes an email or mobile valid — matching the house
 *    render-only rule. Error takes visual precedence over focus (a focused
 *    invalid field still reads as invalid). The email judgment itself lives in
 *    `isValidEmail` (src/utils/validation.ts) so screens share one bar; if
 *    you're here to change what counts as a valid email, that's the file.
 *
 *    WHEN the screen validates is also the screen's call, and the intended
 *    pattern is validate-on-blur / clear-on-focus: don't scare the user
 *    mid-typing, but re-check the moment they leave the field, and drop the
 *    error the moment they come back to fix it. PSInput enables this by
 *    forwarding `onFocus`/`onBlur` up (it still tracks `focused` itself for the
 *    border) — the screen clears `error` in onFocus and re-validates in onBlur.
 *    The atom owns none of that timing; it just surfaces the two moments.
 *
 * `type` is the one behavioral prop: it maps to the right keyboard, autofill and
 * correction behavior for the kind of value being typed, so a call site says
 * `type="email"` rather than juggling `keyboardType`/`autoCapitalize`/etc.
 *
 * There's deliberately NO `type="mobile"`. Mobile numbers are PSMobileInput
 * (`components/ui/PSMobileInput.tsx`): we only accept Indian numbers, so that
 * field has to SHOW the `+91` it prepends, and a visible prefix isn't something
 * this component can grow without becoming configurable. Keeping a bare mobile
 * type here would also be a way to render a mobile field with the country code
 * invisible again, which is exactly what that component exists to prevent.
 */
type PSInputType = 'text' | 'email' | 'code' | 'url';

// Exported so a sibling that must line up with the field (a trailing button)
// sizes off the same number.
export const INPUT_HEIGHT = 44;

type PSInputProps = {
  value: string;
  onChangeText: (value: string) => void;
  /** Drives the keyboard + autocomplete. Defaults to plain text. */
  type?: PSInputType;
  placeholder?: string;
  /**
   * Validation message from the screen. Presence (a non-empty string) flips the
   * field into its error look and renders the message below it. The atom doesn't
   * validate — it's told it's invalid.
   */
  error?: string;
  disabled?: boolean;
  /**
   * Focus the field once the SCREEN it lives on is focused (pops the keyboard).
   * For single-input screens where typing is the obvious next action — e.g. each
   * auth step.
   *
   * We do NOT pass RN's native `autoFocus` down: that fires on mount, which on a
   * pushed/presented screen collides with the routing animation and the focus
   * (and keyboard) silently gets dropped. Instead we focus imperatively from a
   * `useFocusEffect`, which runs after the navigation transition settles — so
   * the keyboard reliably comes up. See the ref/effect below.
   */
  autoFocus?: boolean;
  /** Fired when the field gains focus — the screen clears `error` here. */
  onFocus?: () => void;
  /** Fired when the field loses focus — the screen validates + sets `error` here. */
  onBlur?: () => void;
  /** Disable native spell-check underlines/suggestions. Defaults to the platform default (on). */
  spellCheck?: boolean;
  /** Disable the QuickType predictive/autocorrect bar. Defaults to the platform default (on). */
  autoCorrect?: boolean;
  /** Keyboard return key submits; also turns that key into "Go". */
  onSubmitEditing?: () => void;
};

// type → the native TextInput props that open the right keyboard and set the
// matching autofill/capitalization. Keeping this as a lookup (not inline
// branches) makes it obvious there's exactly one config per type.
const TYPE_PROPS = {
  text: {
    keyboardType: 'default',
    autoCapitalize: 'sentences',
    textContentType: 'none',
  },
  email: {
    keyboardType: 'email-address',
    autoCapitalize: 'none',
    textContentType: 'emailAddress',
  },
  // Coupon codes: no autofill/autocorrect/spellcheck suggestions, and the
  // keyboard shift-locks to caps so typed letters land uppercase.
  code: {
    keyboardType: 'default',
    autoCapitalize: 'characters',
    textContentType: 'none',
    autoComplete: 'off',
    autoCorrect: false,
    spellCheck: false,
  },
  // A link, usually pasted: there's nothing in it to capitalize, autofill,
  // autocorrect or spell-check — every one of those mangles a URL — and the URL
  // keyboard puts "/" and ".com" on the front row for the times it's typed.
  url: {
    keyboardType: 'url',
    autoCapitalize: 'none',
    textContentType: 'none',
    autoComplete: 'off',
    autoCorrect: false,
    spellCheck: false,
  },
} as const;

export default function PSInput({
  value,
  onChangeText,
  type = 'text',
  placeholder,
  error,
  disabled = false,
  autoFocus = false,
  onFocus,
  onBlur,
  spellCheck,
  autoCorrect,
  onSubmitEditing,
}: PSInputProps) {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const inputRef = useRef<TamaguiElement>(null);

  // Programmatic autoFocus (see the `autoFocus` prop docs): focus once the
  // screen is focused rather than on mount, so the keyboard doesn't get eaten by
  // the routing animation. useFocusEffect runs after the transition settles;
  // it also re-focuses if the user navigates back to a screen with this field.
  useFocusEffect(
    useCallback(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [autoFocus])
  );

  // Error wins over focus so an invalid field stays red even while editing.
  const borderColor = hasError ? '$error' : focused ? '$brand' : '$borderColor';

  return (
    <YStack gap="$xs" alignSelf="stretch">
      <Input
        ref={inputRef}
        value={value}
        onChangeText={onChangeText}
        // Track focus for our own border, then forward the moment up so the
        // screen can clear the error on focus / re-validate on blur.
        onFocus={() => {
          setFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        placeholder={placeholder}
        placeholderTextColor="$textDarkMuted"
        // `disabled` is the frame's own prop — on native it maps to the
        // TextInput's non-editable state (RN's `editable` isn't in Input's typed
        // surface). We add the dim ourselves; the frame doesn't.
        disabled={disabled}
        opacity={disabled ? 0.4 : 1}
        spellCheck={spellCheck}
        autoCorrect={autoCorrect}
        onSubmitEditing={onSubmitEditing}
        enterKeyHint={onSubmitEditing ? 'go' : undefined}
        {...TYPE_PROPS[type]}
        // Dimensions/typography pinned explicitly (the `size` token group is
        // intentionally empty — see the docstring), matching PSButton's height.
        height={INPUT_HEIGHT}
        px="$md"
        fontFamily="$body"
        fontSize="$base"
        color="$textDark"
        backgroundColor="$background"
        rounded="$base"
        borderWidth="$borderWidth.hairline"
        borderColor={borderColor}
        // Kill Tamagui's default hover/focus border overrides — we drive the
        // border ourselves off `focused`/`hasError` above.
        hoverStyle={{ borderColor }}
        focusStyle={{ borderColor }}
      />
      {hasError && (
        <Text
          fontFamily="$body"
          fontSize="$small"
          fontWeight={'$semibold'}
          color="$error"
        >
          {error}
        </Text>
      )}
    </YStack>
  );
}
